import React, { useState, useMemo, useEffect } from 'react';
import { Table, Menu, Button } from 'tea-component';
import {
  cancelProjectFollow,
  sortProjectFollow,
  getProjectFollowList,
} from '@gp/services/projectDetail';
import SvgIcon from '@/pages/home/components/SvgIcon';
import * as rbd from 'react-beautiful-dnd';
import FavoriteProjectItem from '../../components/FavoriteProjectItem';
import { eventBus } from '@gp/utils/eventBus';
import './index.scss';
import { notificationTips } from '@gp/utils/common';
import SimpleModal from '@gp/components/SimpleModal';
import NewMarkIcon from '@home/components/NewMarkIcon';
import { MENU_ID_MAP, SLASH_G_POP } from '@gp/constants/const';
import { isEmpty } from 'lodash-es';
import useSearchParams from '@gp/hooks/useSearchParams';
import { history } from 'umi';
import classnames from 'classnames';

interface IFavoriteProjectProps {
  isHyperlink?: boolean;
  selectedFavProItem?: any;
  setSelectedFavProItem?: (item: any) => void;
}

const FavoriteProject: React.FC<IFavoriteProjectProps> = ({
  isHyperlink = false,
  selectedFavProItem,
  setSelectedFavProItem,
}) => {
  const { draggable } = Table.addons;
  const urlParams: any = useSearchParams();
  const [visible, setVisible] = useState(false);
  const [followingInfo, setFollowingInfo] = useState<any>({});
  const [favoriteProjectList, setFavoriteProjectList] = useState<any[]>([]);

  const getFollowingList = async () => {
    try {
      const params = { type: 1 };
      const res = await getProjectFollowList(params);

      if (res?.data) {
        // 收藏
        setFavoriteProjectList(res?.data?.list || []);
      } else {
        setFavoriteProjectList([]);
      }
    } catch (exception) {
    } finally {
    }
  };

  const handleCancelFollowFun = async () => {
    if (followingInfo?.id) {
      try {
        await cancelProjectFollow({ id: followingInfo?.id });
        notificationTips(
          'success',
          `${followingInfo?.project_name} has removed from my Favorite Project.`,
          5000,
        );
        // 通知收藏列表刷新
        eventBus.emit('favorite-project:refresh-project');
        getFollowingList?.(); // 刷新列表
      } catch (error) {
        // message.error('Failed to remove project from favorites');
      }
    }
  };

  const iconDom = () => {
    return <SvgIcon type={'star-m'} className="left-nav-icon" />;
  };

  // 取消收藏
  const handleCancelFollow = async (data: any) => {
    setVisible(true);
    setFollowingInfo(data);
  };

  // 拖拽
  const onDragEnd = async (rec: any) => {
    if (Array.isArray(rec) && rec?.length > 0) {
      try {
        setFavoriteProjectList(rec);
        const newArr = rec?.map((item: any, index: number) => {
          return {
            id: item.id,
            order: index + 1,
          };
        });
        const params = {
          updates: newArr,
        };
        await sortProjectFollow(params);
        getFollowingList?.(); // 刷新列表
      } catch (error) {}
    }
  };

  const toDetails = (item: any, e: any, isJump = true) => {
    if (!item?.project_id) return '';
    const filter = encodeURIComponent(
      JSON.stringify({
        projectId: item?.project_id,
        project: item?.project_name,
        menuFrom: MENU_ID_MAP['pipeline_all-projects'], // 用于标识左侧菜单栏当前选中高亮
        tab: 'gameDetail',
      }),
    );
    const url = `${SLASH_G_POP}/Pipeline/ProjectList/Detail?filter=${filter}`;
    if (isJump) {
      e?.preventDefault();
      e?.stopPropagation();
      setSelectedFavProItem?.(item);
      history.push(url);
    }
    return url;
  };

  useEffect(() => {
    // 获取收藏列表
    getFollowingList();

    // 监听收藏列表刷新事件
    const unsubscribe = eventBus.on('favorite-project:refresh', () => {
      getFollowingList();
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    // 根据url地址变化，判断如果url地址上包含ProjectList/Detail字符串，则setSelectedFavProItem({})
    const { pathname } = location;
    if (!pathname.includes('/ProjectList/Detail')) {
      setSelectedFavProItem?.({});
    } else {
      if (!isEmpty(urlParams.filter)) {
        const paramsObj = JSON.parse?.(urlParams.filter);
        if (
          !isEmpty(paramsObj) &&
          selectedFavProItem?.project_id !== paramsObj?.projectId
        ) {
          const newItem =
            favoriteProjectList.find(
              (item: any) => item.project_id === paramsObj?.projectId,
            ) || {};
          setSelectedFavProItem?.(newItem);
        }
      }
    }
  }, [location, urlParams]);

  // 表格配置
  const COLUMNS: any = [
    {
      key: 'pro_name',
      header: 'Name',
      render: (cvm: any) => {
        return (
          <FavoriteProjectItem
            data={cvm}
            handleCancelFollow={handleCancelFollow}
            toDetails={toDetails}
            isHyperlink={isHyperlink}
          />
        );
      },
    },
  ];

  // 给 tr 设置 class 的函数
  const rowClassName = useMemo(() => {
    return (record: any) => {
      const isSelected =
        selectedFavProItem && selectedFavProItem?.id === record?.id;
      return classnames({
        'is-selected': isSelected,
        'dbg-is-single': favoriteProjectList?.length < 2,
      });
    };
  }, [selectedFavProItem, favoriteProjectList]);

  return (
    <>
      <Menu.SubMenu
        key={'favorite_projects'}
        icon={iconDom()}
        title={
          <div className="left-nav-hd">
            <span className="menu-context-text">{'Favorite Project'}</span>
            <NewMarkIcon
              className={'left-nav-notice db-parent-menu-new'}
              type={0}
            />
          </div>
        }
      >
        <div className="dbg-favorite-projects-submenu">
          <Table
            bordered={false}
            className="dbg-favorite-projects-submenu-table"
            records={favoriteProjectList}
            hideHeader
            rowClassName={rowClassName}
            addons={[
              draggable({
                module: rbd,
                onDragEnd,
              }),
            ]}
            columns={COLUMNS}
          />
        </div>
      </Menu.SubMenu>
      <SimpleModal
        className="dbg-dps-report-submit-confirm-modal"
        title={'Do you want to remove this project from Favorites?'}
        visible={visible}
        description={null}
        size={600}
        onClose={() => {
          setVisible(false);
        }}
        footer={
          <>
            <Button
              type="primary"
              onClick={(e) => {
                e?.stopPropagation();
                setVisible(false);
                handleCancelFollowFun?.();
              }}
            >
              Confirm
            </Button>
            <Button
              onClick={(e) => {
                e?.stopPropagation();
                setVisible(false);
              }}
            >
              Cancel
            </Button>
          </>
        }
      />
    </>
  );
};

export default FavoriteProject;
